'use client';

import { type PropsWithChildren, useCallback } from 'react';
import { useGetAccountPermissionsLazyQuery } from '@frameio/graphql-client/generated';
import { useActiveInboxAccount } from '@frameio/inbox/hooks/useActiveInboxAccount';
import {
  useActiveAccountId,
  useDispatchAppState,
} from '@frameio/shared/contexts';
import { MenuButtonWithPrefetch } from '@frameio/shared/routing/components/MenuButtonWithPrefetch';
import { getLink } from '@frameio/shared/routing/getLink';
import type { MenuButtonProps } from '@frameio/vapor';
import { useHoverIntent } from '@frameio/vapor';

/**
 * @description Renders the menu item  with a dynamic avatar and link to the account settings page
 */
export function UserProfileSettingsLink({
  children,
  disabled,
  ...rest
}: PropsWithChildren<MenuButtonProps>) {
  const accountId = useActiveAccountId();
  const dispatch = useDispatchAppState();
  const { inboxAccount } = useActiveInboxAccount();

  const [executeQuery] = useGetAccountPermissionsLazyQuery({
    variables: { accountId },
  });
  const { bind: hoverIntent } = useHoverIntent(() => executeQuery());

  const href = inboxAccount?.id
    ? getLink({ page: 'inbox', settingsSection: 'profile', accountId })
    : getLink({
        page: 'settings',
        section: 'profile',
      });

  const handleSettingsClick = useCallback(() => {
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
    window.location.assign(href);
  }, [dispatch, href]);

  return disabled ? (
    children
  ) : (
    <MenuButtonWithPrefetch
      href={href}
      {...hoverIntent}
      onClick={handleSettingsClick}
      shouldCloseMenuOnSelect={true}
      {...rest}
    >
      {children}
    </MenuButtonWithPrefetch>
  );
}
